import React, { useState, useRef, useEffect, useMemo } from 'react';
import { isMobile } from '../../constants';
import { Avatar } from '../Avatar';
import { applyFormatToTextarea } from './applyFormat';
import { TextFormatToolbar } from './TextFormatToolbar';
import { enrichMentions } from '../../utils/mentions';
import { noteFilePickerOpened } from '../../utils/appUpdate';
import { LS } from '../../utils/storage';
import { Icon } from '../Icon';
import { useBackClose } from '../../utils/backStack';

export const UploadProgress = ({ progress, onCancel, onRetry, onDismiss }) => {
    if (!progress) return null;
    const { name, percentage, stage } = progress;
    if (stage === 'failed') {
        return (
            <div className="upload-progress-container failed">
                <div className="upload-progress-text">
                    <span>⚠️ {progress.failedCount}개 파일을 올리지 못했습니다{progress.total > progress.failedCount ? ` (나머지 ${progress.total - progress.failedCount}개는 전송됨)` : ''}</span>
                </div>
                {progress.errors?.length > 0 && <div className="upload-error-detail">{progress.errors.join(' · ')}</div>}
                <div className="upload-actions">
                    <button onClick={onRetry}>실패한 파일 다시 올리기</button>
                    <button className="ghost" onClick={onDismiss}>닫기</button>
                </div>
            </div>
        );
    }
    return (
        <div className="upload-progress-container">
            <div className="upload-progress-text">
                <span className="upload-progress-name">{stage === 'preparing' ? '📐 사진 크기 줄이는 중 · ' : ''}{name}</span>
                <span className="upload-progress-right">
                    {Math.round(percentage)}%
                    {percentage < 100 && onCancel && <button className="upload-cancel" onClick={onCancel} aria-label="업로드 취소">취소</button>}
                </span>
            </div>
            <div className="upload-progress-bar-wrapper"><div className={`upload-progress-bar${stage === 'preparing' ? ' preparing' : ''}`} style={{ width: `${stage === 'preparing' ? 100 : percentage}%` }}></div></div>
        </div>
    );
};

// "A님이 입력 중", "A, B님이 입력 중", "A, B님 외 3명이 입력 중"
export const TypingIndicator = ({ users = [] }) => {
    if (!users.length) return null;
    const names = users.slice(0, 2).join(', ');
    const rest = users.length - 2;
    return (
        <div className="typing-indicator" aria-live="polite">
            <span className="typing-dots"><i /><i /><i /></span>
            <span><b>{names}</b>님{rest > 0 ? ` 외 ${rest}명` : ''}이 입력 중</span>
        </div>
    );
};

export const ImagePreviewModal = ({ files, onConfirm, onCancel }) => {
    const [previews, setPreviews] = useState([]);
    const [fileList, setFileList] = useState(files);
    const [original, setOriginal] = useState(() => LS.get('upload_original') === 'true');
    const confirm = () => onConfirm(fileList, { original });
    useBackClose(true, onCancel);
    const hasPhoto = fileList.some(f => /^image\/(jpeg|png|webp)$/.test(f.type));
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') { e.preventDefault(); onConfirm(fileList, { original }); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [fileList, onConfirm, onCancel, original]);
    useEffect(() => {
        const handlePaste = (e) => {
            const items = e.clipboardData.items;
            const filesToProcess = [];
            for (let i = 0; i < items.length; i++) { if (items[i].kind === 'file') { const file = items[i].getAsFile(); if (file) filesToProcess.push(file); } }
            if (filesToProcess.length > 0) {
                e.preventDefault(); e.stopPropagation();
                setFileList(prev => {
                    const sigs = new Set(prev.map(f => `${f.name}:${f.size}:${f.lastModified}`));
                    const unique = filesToProcess.filter(f => { const sig = `${f.name}:${f.size}:${f.lastModified}`; if (sigs.has(sig)) return false; sigs.add(sig); return true; });
                    return unique.length === 0 ? prev : [...prev, ...unique];
                });
            }
        };
        const timer = setTimeout(() => document.addEventListener('paste', handlePaste), 300);
        return () => { clearTimeout(timer); document.removeEventListener('paste', handlePaste); };
    }, []);
    useEffect(() => {
        const newPreviews = fileList.map(file => {
            if (file.type.startsWith('image/') || file.type.startsWith('video/')) return { type: file.type.startsWith('video/') ? 'video' : 'image', url: URL.createObjectURL(file), name: file.name };
            return { type: 'file', name: file.name };
        });
        setPreviews(newPreviews);
        return () => { newPreviews.forEach(p => { if (p.type === 'image' || p.type === 'video') URL.revokeObjectURL(p.url); }); };
    }, [fileList]);
    const moveItem = (index, direction) => {
        const newFiles = [...fileList]; const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newFiles.length) return;
        [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];
        setFileList(newFiles);
    };
    return (
        <div className="modal-overlay">
            <div className="modal-content preview-modal">
                <h3>파일 전송 ({fileList.length}개)</h3>
                <div className="preview-grid-container">
                    <div className="preview-grid">
                        {previews.map((p, idx) => (
                            <div key={idx} className="preview-item-wrapper">
                                <div className="preview-item">
                                    {p.type === 'video' ? <video src={p.url} className="preview-media" muted /> : p.type === 'image' ? <img src={p.url} alt={p.name} className="preview-media" /> : <div className="file-placeholder">📄<br/>{p.name}</div>}
                                    {p.type === 'video' && <div className="video-badge">▶</div>}
                                </div>
                                <div className="reorder-controls">
                                    <button onClick={() => moveItem(idx, -1)} disabled={idx === 0}>◀</button>
                                    <button onClick={() => moveItem(idx, 1)} disabled={idx === previews.length - 1}>▶</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="modal-actions preview-actions">
                    {hasPhoto && (
                        <label className="original-toggle" title="끄면 긴 변 2560px 로 줄여서 빠르게 보냅니다 (위치정보 제거)">
                            <input type="checkbox" checked={original} onChange={e => { setOriginal(e.target.checked); LS.set('upload_original', e.target.checked); }} />
                            원본 화질로 보내기
                        </label>
                    )}
                    <button className="cancel-btn" onClick={onCancel}>취소</button>
                    <button className="confirm-btn" onClick={confirm}>전송</button>
                </div>
            </div>
        </div>
    );
};

// users: [{ userId, username }]
const MentionPopup = ({ users, filter, onSelect, onClose }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const filteredUsers = useMemo(() => {
        const matches = users.filter(u => u.username.toLowerCase().includes(filter.toLowerCase()));
        return [...matches.filter(u => u.isOnline !== false), ...matches.filter(u => u.isOnline === false)];
    }, [users, filter]);
    useEffect(() => { setSelectedIndex(0); }, [filter]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (filteredUsers.length === 0) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => (p + 1) % filteredUsers.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => (p - 1 + filteredUsers.length) % filteredUsers.length); }
            else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); onSelect(filteredUsers[selectedIndex]); }
            else if (e.key === 'Escape') { onClose(); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [filteredUsers, selectedIndex, onSelect, onClose]);
    if (filteredUsers.length === 0) return null;
    return (
        <div className="mention-autocomplete">
            {filteredUsers.map((user, idx) => (
                <div key={user.userId} className={`mention-item ${idx === selectedIndex ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); onSelect(user); }} onMouseEnter={() => setSelectedIndex(idx)}>
                    <Avatar className="mention-avatar" userId={user.userId} name={user.username} />
                    <span style={{ flex: 1 }}>{user.username}</span>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: user.isOnline !== false ? '#3ba55c' : '#747f8d', flexShrink: 0 }} />
                </div>
            ))}
        </div>
    );
};

const ChannelMentionPopup = ({ channels, filter, onSelect, onClose }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const filtered = channels.filter(c => c.name.toLowerCase().includes(filter.toLowerCase()));
    useEffect(() => { setSelectedIndex(0); }, [filter]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (filtered.length === 0) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => (p + 1) % filtered.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => (p - 1 + filtered.length) % filtered.length); }
            else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); onSelect(filtered[selectedIndex]); }
            else if (e.key === 'Escape') { onClose(); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [filtered, selectedIndex, onSelect, onClose]);
    if (filtered.length === 0) return null;
    return (
        <div className="mention-autocomplete channel-autocomplete">
            {filtered.map((ch, idx) => (
                <div key={ch.id} className={`mention-item ${idx === selectedIndex ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); onSelect(ch); }} onMouseEnter={() => setSelectedIndex(idx)}>
                    <span style={{opacity:0.6, fontWeight:700, marginRight:4}}>#</span>{ch.name}
                </div>
            ))}
        </div>
    );
};

const MAX_MSG_LEN = 10000;

export const InputForm = React.forwardRef(({ onSendMessage, onFilesSelected, onlineUsers, myUsername, channels = [], onSelectChannel, onTyping, isAnnouncementChannel, activeEditRef, channelId, onEditLast }, ref) => {
    const fileInputRef = useRef(null);
    const [showPcToolbar, setShowPcToolbar] = useState(false);
    const [isMobileToolbarVisible, setMobileToolbarVisible] = useState(false);
    const [mentionFilter, setMentionFilter] = useState(null);
    const [channelFilter, setChannelFilter] = useState(null);
    const [charCount, setCharCount] = useState(0);
    const [attachOpen, setAttachOpen] = useState(false);
    const attachRef = useRef(null);
    const mediaInputRef = useRef(null);
    useBackClose(attachOpen, () => setAttachOpen(false));
    useEffect(() => setAttachOpen(false), [channelId]);
    useEffect(() => {
        if (!attachOpen) return undefined;
        const close = (e) => { if (!attachRef.current?.contains(e.target)) setAttachOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close, { passive: true });
        return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
    }, [attachOpen]);
    // 팝업에서 선택된 멘션 매핑: username → userId (전송 시 태그 변환에 사용)
    const confirmedMentions = useRef(new Map());
    // 팝업 선택 직후 handleInput에서 mentionFilter를 다시 열지 않도록 방지
    const suppressNextMentionUpdate = useRef(false);

    const adjustHeight = () => {
        const textarea = ref.current;
        if (!textarea) return;
        // 비어 있으면 기본 높이 (빈 칸의 scrollHeight 는 안내 문구 줄바꿈까지 포함돼 커질 수 있음)
        if (!textarea.value) { textarea.style.height = ''; return; }
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    };

    // 채널별 작성 중인 글 보관: 채널을 옮기거나 앱을 닫아도 남아 있다
    const draftTimer = useRef(null);
    const saveDraft = (chId, value) => {
        if (!chId) return;
        if (value && value.trim()) LS.set(`draft_${chId}`, value.slice(0, MAX_MSG_LEN));
        else LS.remove(`draft_${chId}`);
    };
    useEffect(() => {
        const textarea = ref.current;
        if (!textarea || !channelId) return undefined;
        const draft = LS.get(`draft_${channelId}`) || '';
        textarea.value = draft;
        setCharCount(draft.length);
        requestAnimationFrame(adjustHeight);
        return () => {
            clearTimeout(draftTimer.current);
            saveDraft(channelId, textarea.value);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId]);
    useEffect(() => {
        const flush = () => { if (ref.current) saveDraft(channelId, ref.current.value); };
        window.addEventListener('pagehide', flush);
        return () => window.removeEventListener('pagehide', flush);
    }, [channelId, ref]);

    const handleSubmit = () => {
        if (activeEditRef?.current) { activeEditRef.current(); return; }
        const textarea = ref.current;
        if (!textarea) return;
        const raw = textarea.value.trim();
        if (!raw || raw.length > MAX_MSG_LEN) return;
        // @username → @[userId|username] 변환 (팝업 선택 + 직접 입력 모두 처리)
        const enriched = enrichMentions(raw, onlineUsers, confirmedMentions.current);
        onSendMessage(enriched);
        clearTimeout(draftTimer.current);
        LS.remove(`draft_${channelId}`);
        confirmedMentions.current.clear();
        textarea.focus(); textarea.select();
        const ok = document.execCommand('insertText', false, '');
        if (!ok) textarea.value = '';
        textarea.style.height = 'auto';
        setCharCount(0);
        setShowPcToolbar(false); setMentionFilter(null); setChannelFilter(null);
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y' || e.key === 'Z')) return;
        if (mentionFilter !== null || channelFilter !== null) return;
        if (!isMobile && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
        // 빈 입력창에서 ↑ → 내가 보낸 마지막 메시지 수정
        if (e.key === 'ArrowUp' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey && !e.target.value && onEditLast) {
            e.preventDefault();
            onEditLast();
        }
    };

    useEffect(() => {
        if (isMobile) return;
        const checkSelection = () => {
            const textarea = ref.current;
            if (!textarea) return;
            if (document.activeElement !== textarea) { setShowPcToolbar(false); return; }
            setShowPcToolbar(textarea.selectionStart !== textarea.selectionEnd);
        };
        document.addEventListener('selectionchange', checkSelection);
        document.addEventListener('mouseup', checkSelection);
        document.addEventListener('keyup', checkSelection);
        return () => { document.removeEventListener('selectionchange', checkSelection); document.removeEventListener('mouseup', checkSelection); document.removeEventListener('keyup', checkSelection); };
    }, [ref]);

    const handleFormat = (type) => {
        const textarea = ref.current;
        if (!textarea) return;
        applyFormatToTextarea(textarea, type, textarea.value, (newVal) => { textarea.value = newVal; adjustHeight(); });
        setShowPcToolbar(false);
    };

    const handleInput = (e) => {
        const val = e.target.value; const selStart = e.target.selectionStart;
        setCharCount(val.length);
        clearTimeout(draftTimer.current);
        draftTimer.current = setTimeout(() => saveDraft(channelId, val), 400);
        adjustHeight();
        if (onTyping && val.length > 0) onTyping();
        if (suppressNextMentionUpdate.current) { suppressNextMentionUpdate.current = false; return; }
        const textBeforeCursor = val.slice(0, selStart);
        // 공백 없는 @word 만 팝업 트리거 (공백 이후엔 팝업 닫힘)
        const mentionMatch = textBeforeCursor.match(/@([^\s@\n]*)$/);
        const channelMatch = textBeforeCursor.match(/#([a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ_]*)$/);
        if (mentionMatch) { setMentionFilter(mentionMatch[1]); setChannelFilter(null); }
        else if (channelMatch) { setChannelFilter(channelMatch[1]); setMentionFilter(null); }
        else { setMentionFilter(null); setChannelFilter(null); }
    };

    const handleChannelSelect = (ch) => {
        const textarea = ref.current; if (!textarea) return;
        const selStart = textarea.selectionStart; const val = textarea.value;
        const match = val.slice(0, selStart).match(/#([a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ_]*)$/);
        if (match) {
            const insertion = `#${ch.name} `;
            textarea.focus(); textarea.setSelectionRange(match.index, selStart);
            const ok = document.execCommand('insertText', false, insertion);
            if (!ok) textarea.value = val.slice(0, match.index) + insertion + val.slice(selStart);
            setChannelFilter(null); adjustHeight();
            setTimeout(() => textarea.setSelectionRange(match.index + insertion.length, match.index + insertion.length), 0);
        }
    };

    // 팝업에서 user 선택: textarea엔 @username만 삽입, userId는 confirmedMentions에 저장
    const handleMentionSelect = (user) => {
        const textarea = ref.current; if (!textarea) return;
        const selStart = textarea.selectionStart; const val = textarea.value;
        const atIndex = val.slice(0, selStart).lastIndexOf('@');
        if (atIndex === -1) return;
        const insertion = `@${user.username} `;
        confirmedMentions.current.set(user.username, user.userId);
        suppressNextMentionUpdate.current = true;
        textarea.focus(); textarea.setSelectionRange(atIndex, selStart);
        const ok = document.execCommand('insertText', false, insertion);
        if (!ok) textarea.value = val.slice(0, atIndex) + insertion + val.slice(selStart);
        setMentionFilter(null); adjustHeight();
        const newPos = atIndex + insertion.length;
        setTimeout(() => textarea.setSelectionRange(newPos, newPos), 0);
    };

    useEffect(() => {
        const textarea = ref.current;
        const handlePaste = (e) => {
            const files = [];
            for (let i = 0; i < e.clipboardData.items.length; i++) { if (e.clipboardData.items[i].kind === 'file') { const file = e.clipboardData.items[i].getAsFile(); if (file) files.push(file); } }
            if (files.length > 0) { e.preventDefault(); e.stopPropagation(); onFilesSelected(files); }
        };
        if (textarea) textarea.addEventListener('paste', handlePaste);
        return () => { if (textarea) textarea.removeEventListener('paste', handlePaste); };
    }, [onFilesSelected, ref]);

    const openFilePicker = (input = fileInputRef) => { setAttachOpen(false); noteFilePickerOpened(); input.current.click(); };
    const handleFileChange = (e) => { if (e.target.files?.length > 0) onFilesSelected(Array.from(e.target.files)); e.target.value = ''; };

    const isNearLimit = charCount > MAX_MSG_LEN * 0.8;
    const isOverLimit = charCount > MAX_MSG_LEN;

    const hasText = charCount > 0;
    return (
        <div className="input-area-container">
            {mentionFilter !== null && <MentionPopup users={onlineUsers.filter(u => u.username !== myUsername)} filter={mentionFilter} onSelect={handleMentionSelect} onClose={() => setMentionFilter(null)} />}
            {channelFilter !== null && <ChannelMentionPopup channels={channels} filter={channelFilter} onSelect={handleChannelSelect} onClose={() => setChannelFilter(null)} />}
            {!isMobile && <TextFormatToolbar show={showPcToolbar} onFormat={handleFormat} />}
            {isMobile && isMobileToolbarVisible && <TextFormatToolbar isMobileToolbar={true} show={true} onFormat={handleFormat} />}
            <form id="form" onSubmit={e => e.preventDefault()}>
                {/* ＋ 첨부 메뉴: 사진·동영상 / 파일 / (휴대폰) 글자 서식 */}
                <div className="attach-wrap" ref={attachRef}>
                    <button type="button" id="fileBtn" className={attachOpen ? 'active' : ''} onClick={() => setAttachOpen(v => !v)}
                        title="첨부" aria-label="첨부 메뉴" aria-haspopup="menu" aria-expanded={attachOpen}><Icon name="plus" size={22} /></button>
                    {attachOpen && (
                        <div className="attach-menu" role="menu">
                            <button type="button" role="menuitem" className="menu-item" onClick={() => openFilePicker(mediaInputRef)}><Icon name="image" size={18} /><span>사진·동영상</span></button>
                            <button type="button" role="menuitem" className="menu-item" onClick={() => openFilePicker(fileInputRef)}><Icon name="paperclip" size={18} /><span>파일</span></button>
                            {isMobile && (
                                <button type="button" role="menuitem" className="menu-item" onMouseDown={e => e.preventDefault()}
                                    onClick={() => { setAttachOpen(false); setMobileToolbarVisible(v => !v); ref.current?.focus(); }}>
                                    <Icon name="code" size={18} /><span>{isMobileToolbarVisible ? '글자 서식 닫기' : '글자 서식'}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <textarea ref={ref} id="message" rows="1" placeholder={isAnnouncementChannel ? '📢 공지 전용 채널 — 메시지를 보낼 수 없습니다.' : '메시지 (@멘션)'} defaultValue="" onChange={handleInput} onKeyDown={handleKeyDown} onBlur={() => { setTimeout(() => {
                    if (!isMobile) setShowPcToolbar(false);
                    // 서식 버튼을 누르는 동안엔 포커스가 유지되므로, 정말로 입력창을 벗어났을 때만 모바일 서식 줄을 닫는다
                    if (document.activeElement !== ref.current) setMobileToolbarVisible(false);
                }, 200); }} readOnly={isAnnouncementChannel} className={isAnnouncementChannel ? 'readonly' : undefined} />
                <input type="file" ref={fileInputRef} onChange={handleFileChange} hidden multiple />
                <input type="file" ref={mediaInputRef} onChange={handleFileChange} hidden multiple accept="image/*,video/*" />
                <button id="send" type="button" className={hasText ? '' : 'empty'} onClick={handleSubmit} disabled={isOverLimit}
                    title={isMobile ? '보내기' : '보내기 (Enter)'} aria-label="보내기">{isMobile ? '✔️' : '전송'}</button>
            </form>
            {isNearLimit && <div className={`char-count${isOverLimit ? ' over' : ''}`}>{charCount.toLocaleString()}/{MAX_MSG_LEN.toLocaleString()}</div>}
        </div>
    );
});
